/**
 * Preparations — the full library of four nervous-system regulation practices.
 *
 * Accessible via /preparations. Linked from the Option B home card ("See all").
 * Each card shows name, tagline, duration, and opens the guided experience.
 *
 * Tier gating (Phase 2):
 *   Free: Orienting + Heart Breathing
 *   Practice + Awakening: all four
 * For now all four are unlocked — gating wires in once DB tier check is added.
 */
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PREPARATIONS } from '../data/preparations'
import { localizePreparation } from '../data/preparations.pl'
import type { Preparation } from '../data/preparations'

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M12.5 15l-5-5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M6 3.5V6l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M4 7h6M7.5 4.5L10 7l-2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PreparationCard({ prep, onSelect }: { prep: Preparation; onSelect: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={onSelect}
      className="w-full text-left transition-all hover:opacity-90 active:scale-[0.99] rounded-2xl"
      style={{
        background: prep.colorSoft,
        borderTop: `3px solid ${prep.color}`,
        borderRight: '1.5px solid #E4D5EE',
        borderBottom: '1.5px solid #E4D5EE',
        borderLeft: '1.5px solid #E4D5EE',
        cursor: 'pointer',
      }}
    >
      <div className="px-5 py-5">
        {/* Header row */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <p
              className="text-xs font-medium uppercase tracking-widest mb-1"
              style={{ color: prep.color }}
            >
              {t('preparations.preparation')}
            </p>
            <p
              className="font-display text-xl leading-snug"
              style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}
            >
              {prep.name}
            </p>
          </div>

          {/* Duration badge */}
          <div
            className="flex items-center gap-1 px-2.5 py-1 rounded-full mt-1 flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.7)', border: `1px solid ${prep.color}30` }}
          >
            <ClockIcon />
            <span className="text-xs font-medium" style={{ color: prep.color }}>
              {prep.durationMinutes} min
            </span>
          </div>
        </div>

        {/* Tagline */}
        <p className="text-sm leading-relaxed mb-1" style={{ color: '#6B5F7A' }}>
          {prep.tagline}
        </p>

        {/* Description */}
        <p className="text-xs leading-relaxed mb-4" style={{ color: '#9B8FAA' }}>
          {prep.description}
        </p>

        {/* Regulation note */}
        <div
          className="mb-4 px-3 py-2 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.5)', border: `1px solid ${prep.color}20` }}
        >
          <p className="text-xs leading-relaxed" style={{ color: '#6B5F7A', fontStyle: 'italic' }}>
            {prep.regulation}
          </p>
        </div>

        {/* Begin CTA */}
        <div className="flex items-center justify-end gap-1">
          <span className="text-xs font-medium" style={{ color: prep.color }}>{t('common.begin')}</span>
          <ArrowIcon />
        </div>
      </div>
    </button>
  )
}

export default function Preparations() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  // The practice copy lives in code, not in the locale file, so it has to be
  // swapped here. Only Home did this before, which left this list in English
  // for a Polish reader.
  const preparations = PREPARATIONS.map(prep => localizePreparation(prep, i18n.language))

  return (
    <div
      className="min-h-svh flex flex-col"
      style={{
        background: 'radial-gradient(ellipse at 60% 0%, rgba(58,155,122,0.07) 0%, transparent 50%), #FAF7F4',
      }}
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-6 py-5 max-w-xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          className="hover:opacity-70 transition-opacity"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
          aria-label={t('preparations.back')}
        >
          <BackIcon />
        </button>
        <div>
          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#3A9B7A' }}>
            {t('preparations.yourToolkit')}
          </p>
          <h1 className="font-display text-lg text-[#1C1128] font-normal" style={{ fontStyle: 'italic' }}>
            {t('preparations.title')}
          </h1>
        </div>
      </header>

      {/* Intro copy */}
      <div className="px-6 pb-4 max-w-xl mx-auto w-full">
        <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
          {t('preparations.introFull')}
        </p>
      </div>

      {/* Preparation cards */}
      <main className="flex-1 px-6 pb-16 max-w-xl mx-auto w-full page-enter">
        <div className="flex flex-col gap-4">
          {preparations.map(prep => (
            <PreparationCard
              key={prep.id}
              prep={prep}
              onSelect={() => navigate(`/preparation/${prep.id}`)}
            />
          ))}
        </div>
      </main>
    </div>
  )
}
